import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { downloadBlob } from "@/lib/download";
import PageHeader from "@/components/PageHeader";
import MemberAvatar from "@/components/MemberAvatar";
import FeedView from "@/components/FeedView";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Lock, Anchor, Users, Newspaper, FolderLock, ClipboardList, Download, Crown, MapPin, Plus, X, Mail, Phone, Cake, Sailboat } from "lucide-react";
import Crest from "@/components/Crest";
import { toast } from "sonner";

const FRIGATE_ART = {
  Cherokee: { emoji: "🏹", tint: "from-amber-500/25 to-[hsl(var(--gold))]/10" },
  Cumberland: { emoji: "⚓", tint: "from-[hsl(var(--water))]/25 to-cyan-500/10" },
  Pelican: { emoji: "🕊️", tint: "from-emerald-500/25 to-teal-500/10" },
};

function MemberDialog({ m, onClose }) {
  return (
    <Dialog open={!!m} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="glass">
        {m && (
          <>
            <DialogHeader><DialogTitle className="sr-only">{m.name}</DialogTitle></DialogHeader>
            <div className="flex flex-col items-center text-center">
              <Avatar className="h-24 w-24 ring-2 ring-[hsl(var(--gold))]/40">
                <MemberAvatar name={m.name} photo={m.photo_path} className="h-24 w-24" />
              </Avatar>
              <h2 className="mt-3 font-display text-2xl font-700">{m.name}</h2>
              {m.deck_name && <p className="text-sm italic text-muted-foreground">Deck name: "{m.deck_name}"</p>}
              <div className="mt-2 flex gap-2">
                <Badge className="bg-[hsl(var(--gold))] text-[hsl(var(--primary-foreground))]">{m.position}</Badge>
                <Badge variant="outline" className="border-[hsl(var(--water))]/40 text-[hsl(var(--water))]"><Sailboat className="mr-1 h-3 w-3" />{m.frigate}</Badge>
              </div>
              {m.bio && <p className="mt-4 text-sm text-muted-foreground">{m.bio}</p>}
              <div className="mt-5 w-full space-y-2 text-left text-sm">
                <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-[hsl(var(--water))]" /> {m.email}</div>
                {m.phone && <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[hsl(var(--water))]" /> {m.phone}</div>}
                {m.birthday && <div className="flex items-center gap-2"><Cake className="h-4 w-4 text-pink-500" /> {m.birthday}</div>}
              </div>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Roster({ frigate }) {
  const [members, setMembers] = useState([]);
  const [sel, setSel] = useState(null);
  useEffect(() => { api.get(`/frigates/${frigate}/members`).then((r) => setMembers(r.data.members)).catch(() => setMembers([])); }, [frigate]);
  return (
    <>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid={`roster-${frigate}`}>
        {members.map((m) => (
          <Card key={m.id} onClick={() => setSel(m)} className="gold-ring glass flex cursor-pointer items-center gap-3 p-4" data-testid={`frigate-member-${m.id}`}>
            <Crest position={m.position} size={28} className="order-last ml-auto" />
            <Avatar className="h-12 w-12 ring-2 ring-[hsl(var(--gold))]/30">
              <MemberAvatar name={m.name} photo={m.photo_path} className="h-12 w-12" />
            </Avatar>
            <div className="min-w-0">
              <p className="truncate text-sm font-700">{m.name}{m.position === "Frigate Captain" && <Crown className="ml-1 inline h-3.5 w-3.5 text-[hsl(var(--gold))]" />}</p>
              {m.deck_name && <p className="text-xs italic text-muted-foreground">"{m.deck_name}"</p>}
              <Badge variant="outline" className="mt-1 border-[hsl(var(--water))]/40 text-[0.65rem] text-[hsl(var(--water))]">{m.position}</Badge>
            </div>
          </Card>
        ))}
        {members.length === 0 && <p className="text-sm text-muted-foreground">No brothers assigned yet.</p>}
      </div>
      <MemberDialog m={sel} onClose={() => setSel(null)} />
    </>
  );
}

function StatesCovered({ frigate, canEdit }) {
  const [states, setStates] = useState([]);
  const [val, setVal] = useState("");
  useEffect(() => { api.get(`/frigates/${frigate}/states`).then((r) => setStates(r.data.states)); }, [frigate]);
  const save = async (next) => {
    try { const r = await api.put(`/frigates/${frigate}/states`, { states: next }); setStates(r.data.states); toast.success("States updated"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const add = () => { if (!val.trim()) return; save([...states, val.trim()]); setVal(""); };
  return (
    <Card className="gold-ring glass p-5" data-testid={`states-${frigate}`}>
      <div className="mb-3 flex items-center gap-2"><MapPin className="h-5 w-5 text-[hsl(var(--water))]" /><h3 className="font-display text-lg font-700">States Covered</h3></div>
      <div className="flex flex-wrap gap-2">
        {states.map((s) => (
          <Badge key={s} variant="outline" className="border-[hsl(var(--gold))]/40 py-1 text-sm" data-testid={`state-${s}`}>
            {s}{canEdit && <button onClick={() => save(states.filter((x) => x !== s))} className="ml-1.5 text-muted-foreground hover:text-destructive" data-testid={`remove-state-${s}`}><X className="h-3 w-3" /></button>}
          </Badge>
        ))}
        {states.length === 0 && <p className="text-sm text-muted-foreground">States covered will be listed here soon.</p>}
      </div>
      {canEdit && (
        <div className="mt-4 flex gap-2">
          <Input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add a state (e.g. Lagos)" className="max-w-xs" data-testid="state-input" />
          <Button onClick={add} className="bg-[hsl(var(--water))] text-white" data-testid="add-state-btn"><Plus className="mr-1 h-4 w-4" />Add</Button>
        </div>
      )}
    </Card>
  );
}

function FrigateDocs({ frigate }) {
  const [docs, setDocs] = useState([]);
  useEffect(() => { api.get("/documents", { params: { frigate } }).then((r) => setDocs(r.data.documents)).catch(() => setDocs([])); }, [frigate]);
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {docs.map((d) => (
        <Card key={d.id} className="gold-ring glass flex items-center justify-between p-4" data-testid={`frigate-doc-${d.id}`}>
          <div><p className="text-sm font-700">{d.title}</p><p className="text-xs text-muted-foreground">{d.category}</p></div>
          <Button size="sm" className="bg-[hsl(var(--water))] text-white" onClick={() => downloadBlob(`/documents/${d.id}/download`, `${d.title}.${d.ext || "pdf"}`)}><Download className="mr-1.5 h-4 w-4" />Get</Button>
        </Card>
      ))}
      {docs.length === 0 && <p className="text-sm text-muted-foreground">No private documents in this frigate yet.</p>}
    </div>
  );
}

function FrigateTasks({ frigate }) {
  const [tasks, setTasks] = useState([]);
  useEffect(() => { api.get("/tasks").then((r) => setTasks(r.data.tasks.filter((t) => t.frigate === frigate))).catch(() => {}); }, [frigate]);
  const cols = [["todo", "To Do"], ["in_progress", "In Progress"], ["done", "Done"]];
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {cols.map(([key, label]) => (
        <div key={key}>
          <h4 className="mb-2 font-display text-sm font-700 uppercase tracking-wider text-muted-foreground">{label}</h4>
          <div className="space-y-2">
            {tasks.filter((t) => t.status === key).map((t) => (
              <Card key={t.id} className="gold-ring glass p-3"><p className="text-sm font-600">{t.title}</p><p className="mt-1 text-xs text-muted-foreground">{t.assignee_name}{t.deadline ? ` · ${t.deadline}` : ""}</p></Card>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function FrigatePanel({ f }) {
  const { user, hasRole } = useAuth();
  const art = FRIGATE_ART[f.name] || { emoji: "⚓", tint: "from-slate-500/20 to-slate-500/5" };
  const canEdit = user?.is_galleon_admin || user?.role === "super_admin" || (user?.frigate === f.name && user?.position === "Frigate Captain");
  return (
    <div className="space-y-6" data-testid={`frigate-panel-${f.name}`}>
      <Card className={`gold-ring glass flex flex-wrap items-center gap-5 bg-gradient-to-br ${art.tint} p-6`}>
        <span className="animate-float text-6xl">{art.emoji}</span>
        <div className="flex-1">
          <h2 className="font-display text-3xl font-700">{f.name} Frigate</h2>
          <p className="mt-1 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5"><Users className="h-4 w-4" />{f.member_count} brothers</span>
            <span className="flex items-center gap-1.5"><Crown className="h-4 w-4 text-[hsl(var(--gold))]" />Captain: {f.captain || "—"}</span>
          </p>
        </div>
        {!f.is_member && <Badge variant="outline" className="gold-ring"><Lock className="mr-1 h-3 w-3" />Private workspace</Badge>}
      </Card>

      <section>
        <h3 className="mb-3 font-display text-lg font-700">Members</h3>
        {f.is_member ? <Roster frigate={f.name} /> : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {f.officers.map((m) => (
              <Card key={m.id} className="gold-ring glass flex items-center gap-3 p-4" data-testid={`frigate-officer-${m.id}`}>
                <Avatar className="h-12 w-12 ring-2 ring-[hsl(var(--gold))]/30">
                  <MemberAvatar name={m.name} photo={m.photo_path} className="h-12 w-12" />
                </Avatar>
                <div className="min-w-0"><p className="truncate text-sm font-700">{m.name}</p><Badge variant="outline" className="mt-1 border-[hsl(var(--water))]/40 text-[0.65rem] text-[hsl(var(--water))]">{m.position}</Badge></div>
              </Card>
            ))}
            <p className="col-span-full text-xs text-muted-foreground"><Lock className="mr-1 inline h-3 w-3" />Full roster is private to {f.name} crew. Officers shown only.</p>
          </div>
        )}
      </section>

      <StatesCovered frigate={f.name} canEdit={canEdit} />

      {f.is_member && (
        <Tabs defaultValue="feed">
          <TabsList className="glass mb-4">
            <TabsTrigger value="feed" data-testid="frigate-tab-feed"><Newspaper className="mr-1.5 h-4 w-4" />Private Feed</TabsTrigger>
            <TabsTrigger value="docs" data-testid="frigate-tab-docs"><FolderLock className="mr-1.5 h-4 w-4" />Documents</TabsTrigger>
            {hasRole("officer") && <TabsTrigger value="tasks" data-testid="frigate-tab-tasks"><ClipboardList className="mr-1.5 h-4 w-4" />Tasks</TabsTrigger>}
          </TabsList>
          <TabsContent value="feed"><FeedView scope="frigate" frigate={f.name} showReel showRegions={false} /></TabsContent>
          <TabsContent value="docs"><FrigateDocs frigate={f.name} /></TabsContent>
          {hasRole("officer") && <TabsContent value="tasks"><FrigateTasks frigate={f.name} /></TabsContent>}
        </Tabs>
      )}
    </div>
  );
}

export default function Frigates() {
  const { user } = useAuth();
  const [frigates, setFrigates] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    api.get("/frigates").then((r) => { setFrigates(r.data.frigates); setActive(r.data.my_frigate || r.data.frigates[0]?.name); });
  }, []);

  if (!active) return null;
  return (
    <div>
      <PageHeader eyebrow="The Three Frigates" title="Frigates"
        description="Select a frigate to see its members, the states it covers, and — for crew — its private workspace." />
      <Tabs value={active} onValueChange={setActive}>
        <TabsList className="glass mb-6 h-auto flex-wrap" data-testid="frigate-selector">
          {frigates.map((f) => (
            <TabsTrigger key={f.name} value={f.name} className="px-5 py-2.5 text-base" data-testid={`frigate-select-${f.name}`}>
              <span className="mr-2">{FRIGATE_ART[f.name]?.emoji}</span>{f.name}
              {user?.frigate === f.name && <Badge className="ml-2 bg-[hsl(var(--gold))] text-[0.6rem] text-[hsl(var(--primary-foreground))]">Mine</Badge>}
            </TabsTrigger>
          ))}
        </TabsList>
        {frigates.map((f) => <TabsContent key={f.name} value={f.name}><FrigatePanel f={f} /></TabsContent>)}
      </Tabs>
      <p className="mt-6 flex items-center gap-2 text-xs text-muted-foreground"><Anchor className="h-3.5 w-3.5" />Galleon administrators can enter any frigate; brothers see the full workspace of their own frigate only.</p>
    </div>
  );
}
